import "../loot-rate-display-mode.js";

import {
    CALCULATOR_METRIC_UNIT_MARK_NONE,
    CALCULATOR_METRIC_UNIT_MARK_PERCENT,
    CALCULATOR_METRIC_UNIT_MARK_RAW_WEIGHT,
} from "./calculator-metric-unit-mark.js";

const DISPLAY_MODE_CONTRACT = globalThis.__fishystuffLootRateDisplayModeContract;
if (!DISPLAY_MODE_CONTRACT || typeof DISPLAY_MODE_CONTRACT.parse !== "function") {
    throw new Error("Loot rate display mode contract is unavailable");
}
export const CALCULATOR_LOOT_RATE_DISPLAY_MODES = DISPLAY_MODE_CONTRACT.VALUES;

const DENORMALIZED_WEIGHT_PRESENTATION = Object.freeze({
    basis: "denormalizedWeight",
    unitMark: CALCULATOR_METRIC_UNIT_MARK_RAW_WEIGHT,
});

const RATE_BASIS_PRESENTATIONS = Object.freeze({
    percent: Object.freeze({
        basis: "percent",
        unitMark: CALCULATOR_METRIC_UNIT_MARK_PERCENT,
    }),
    denormalizedWeight: DENORMALIZED_WEIGHT_PRESENTATION,
    rawWeight: Object.freeze({
        basis: "rawWeight",
        unitMark: CALCULATOR_METRIC_UNIT_MARK_RAW_WEIGHT,
    }),
});
const EDITABLE_RATE_BASES = Object.freeze([
    "percent",
    "denormalizedWeight",
    "rawWeight",
]);
const DENORMALIZED_WEIGHT_DECIMAL_SHIFT = 4;

function numberFormatOptions(value) {
    if (value === undefined) {
        return { minimumFractionDigits: 0, maximumFractionDigits: 6 };
    }
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError("loot rate numberFormat must be an object");
    }
    const minimumFractionDigits = value.minimumFractionDigits ?? 0;
    const maximumFractionDigits = value.maximumFractionDigits ?? 6;
    if (
        !Number.isInteger(minimumFractionDigits)
        || !Number.isInteger(maximumFractionDigits)
        || minimumFractionDigits < 0
        || maximumFractionDigits < minimumFractionDigits
        || maximumFractionDigits > 20
    ) {
        throw new TypeError("loot rate fraction digits must be ordered integers from 0 through 20");
    }
    return { minimumFractionDigits, maximumFractionDigits };
}

export function calculatorLootRateBasisPresentation(basis) {
    if (basis === "ratio" || basis === "percent") {
        return RATE_BASIS_PRESENTATIONS.percent;
    }
    if (basis === "denormalizedWeight") {
        return RATE_BASIS_PRESENTATIONS.denormalizedWeight;
    }
    if (basis === "rawWeight") {
        return RATE_BASIS_PRESENTATIONS.rawWeight;
    }
    throw new TypeError(`unsupported loot rate basis: ${basis}`);
}

function lootRateDisplay(
    rate,
    {
        locale = "en-US",
        numberFormat,
        presentationBasis = null,
        unknownText = "Unknown",
        unavailableText = "Unavailable",
    } = {},
    {
        forcePercentMaximumFractionDigits = false,
        includeRoundedTitle = false,
        maximumDenormalizedWeightFractionDigits = 4,
        maximumPercentFractionDigits = 2,
        maximumRawWeightFractionDigits = 0,
    } = {},
) {
    if (
        !Number.isInteger(maximumPercentFractionDigits)
        || maximumPercentFractionDigits < 0
        || maximumPercentFractionDigits > 20
    ) {
        throw new TypeError("loot rate percent precision must be an integer from 0 through 20");
    }
    if (
        !Number.isInteger(maximumDenormalizedWeightFractionDigits)
        || maximumDenormalizedWeightFractionDigits < 0
        || maximumDenormalizedWeightFractionDigits > 20
    ) {
        throw new TypeError("loot denormalized-weight precision must be an integer from 0 through 20");
    }
    if (
        !Number.isInteger(maximumRawWeightFractionDigits)
        || maximumRawWeightFractionDigits < 0
        || maximumRawWeightFractionDigits > 20
    ) {
        throw new TypeError("loot raw-weight precision must be an integer from 0 through 20");
    }
    const state = rate?.state ?? "known";
    const basis = presentationBasis === null
        ? (typeof rate?.basis === "string"
            ? calculatorLootRateBasisPresentation(rate.basis)
            : null)
        : calculatorLootRateBasisPresentation(presentationBasis);
    if (state === "unknown" || state === "unavailable") {
        return Object.freeze({
            state,
            text: state === "unknown" ? String(unknownText) : String(unavailableText),
            basis: basis?.basis ?? null,
            unitMark: basis?.unitMark ?? CALCULATOR_METRIC_UNIT_MARK_NONE,
            ...(includeRoundedTitle ? { titleText: null } : {}),
        });
    }
    if (
        state !== "known"
        || !Number.isFinite(rate?.value)
        || rate.value < 0
    ) {
        throw new TypeError("known loot rate display values must be finite and non-negative");
    }
    if (basis === null) {
        throw new TypeError("known loot rate display values must provide a supported basis");
    }
    const value = rate.basis === "ratio" ? rate.value * 100 : rate.value;
    const configured = numberFormatOptions(numberFormat);
    const maximumFractionDigits = basis.basis === "rawWeight"
        ? maximumRawWeightFractionDigits
        : basis.basis === "denormalizedWeight"
            ? maximumDenormalizedWeightFractionDigits
            : forcePercentMaximumFractionDigits
                ? maximumPercentFractionDigits
                : Math.min(maximumPercentFractionDigits, configured.maximumFractionDigits);
    const minimumFractionDigits = Math.min(
        maximumFractionDigits,
        configured.minimumFractionDigits,
    );
    const formattedValue = new Intl.NumberFormat(locale, {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(value);
    const text = `${formattedValue}${basis.basis === "denormalizedWeight" ? "%" : ""}`;
    let titleText = null;
    if (includeRoundedTitle) {
        const suffix = basis.basis === "rawWeight" ? "" : "%";
        const fullText = `${new Intl.NumberFormat(locale, {
            maximumSignificantDigits: 21,
        }).format(value)}${suffix}`;
        const roundedValue = Number(value.toFixed(maximumFractionDigits));
        titleText = roundedValue === value ? null : fullText;
    }
    return Object.freeze({
        state: "known",
        text,
        basis: basis.basis,
        unitMark: basis.unitMark,
        ...(includeRoundedTitle ? { titleText } : {}),
    });
}

export function calculatorLootRateDisplay(rate, options = {}) {
    return lootRateDisplay(rate, options);
}

export function calculatorLootItemRateDisplay(rate, options = {}) {
    return lootRateDisplay(
        rate,
        options,
        {
            forcePercentMaximumFractionDigits: true,
            includeRoundedTitle: true,
            maximumDenormalizedWeightFractionDigits: 2,
            maximumPercentFractionDigits: 2,
            maximumRawWeightFractionDigits: 2,
        },
    );
}

export function parseCalculatorLootRateDisplayMode(
    value,
    path = "loot rate display mode",
) {
    return DISPLAY_MODE_CONTRACT.parse(value, path);
}

export function calculatorLootRateForDisplayMode(
    normalizedRate,
    denormalizedRate,
    rawWeight,
    mode,
) {
    const parsedMode = parseCalculatorLootRateDisplayMode(mode);
    if (parsedMode === DISPLAY_MODE_CONTRACT.NORMALIZED) {
        return Object.freeze({
            presentationBasis: "percent",
            rate: normalizedRate,
        });
    }
    if (parsedMode === DISPLAY_MODE_CONTRACT.DENORMALIZED) {
        return Object.freeze({
            presentationBasis: "denormalizedWeight",
            rate: denormalizedRate,
        });
    }
    return Object.freeze({
        presentationBasis: "rawWeight",
        rate: rawWeight,
    });
}

export function calculatorLootRateDisplayForMode(
    normalizedRate,
    denormalizedRate,
    rawWeight,
    mode,
    options = {},
) {
    const selected = calculatorLootRateForDisplayMode(
        normalizedRate,
        denormalizedRate,
        rawWeight,
        mode,
    );
    return calculatorLootRateDisplay(selected.rate, {
        ...options,
        presentationBasis: selected.presentationBasis,
    });
}

export function calculatorLootItemRateDisplayForMode(
    normalizedRate,
    denormalizedRate,
    rawWeight,
    mode,
    options = {},
) {
    const selected = calculatorLootRateForDisplayMode(
        normalizedRate,
        denormalizedRate,
        rawWeight,
        mode,
    );
    return calculatorLootItemRateDisplay(selected.rate, {
        ...options,
        presentationBasis: selected.presentationBasis,
    });
}

function decimalInputParts(value) {
    if (!Number.isFinite(value) || value < 0) {
        throw new TypeError("editable loot rate values must be finite and non-negative");
    }
    const [coefficient, exponentText = "0"] = String(value).toLowerCase().split("e");
    const [integer, fraction = ""] = coefficient.split(".");
    return Object.freeze({
        decimalPosition: integer.length + Number(exponentText),
        digits: `${integer}${fraction}`,
    });
}

function decimalInputAt({ decimalPosition, digits }, shift = 0) {
    const position = decimalPosition + shift;
    let whole;
    let fraction;
    if (position <= 0) {
        whole = "0";
        fraction = `${"0".repeat(-position)}${digits}`;
    } else if (position >= digits.length) {
        whole = `${digits}${"0".repeat(position - digits.length)}`;
        fraction = "";
    } else {
        whole = digits.slice(0, position);
        fraction = digits.slice(position);
    }
    whole = whole.replace(/^0+(?=\d)/u, "");
    fraction = fraction.replace(/0+$/u, "");
    return fraction ? `${whole}.${fraction}` : whole;
}

function editableRateInput(rate, defaultBasis) {
    if (rate?.state !== "known") {
        return Object.freeze({
            basis: defaultBasis,
            value: "",
        });
    }
    const parts = decimalInputParts(rate.value);
    if (rate.basis === "rawWeight") {
        return Object.freeze({
            basis: "rawWeight",
            value: decimalInputAt(parts),
        });
    }
    if (rate.basis === "percent") {
        return Object.freeze({
            basis: "percent",
            value: decimalInputAt(parts),
        });
    }
    if (rate.basis === "denormalizedWeight") {
        return Object.freeze({
            basis: "denormalizedWeight",
            value: decimalInputAt(parts),
        });
    }
    if (rate.basis === "ratio") {
        return Object.freeze({
            basis: "percent",
            value: decimalInputAt(parts, 2),
        });
    }
    throw new TypeError(`unsupported editable loot rate basis: ${rate.basis}`);
}

export function calculatorLootEditableRateInputs(
    rate,
    {
        defaultBasis = "rawWeight",
        preferredBasis = null,
    } = {},
) {
    if (!EDITABLE_RATE_BASES.includes(defaultBasis)) {
        throw new TypeError(`unsupported default editable loot rate basis: ${defaultBasis}`);
    }
    if (preferredBasis !== null && !EDITABLE_RATE_BASES.includes(preferredBasis)) {
        throw new TypeError(`unsupported preferred editable loot rate basis: ${preferredBasis}`);
    }
    const inputs = Object.fromEntries(EDITABLE_RATE_BASES.map((basis) => [
        basis,
        Object.freeze({ basis, value: "" }),
    ]));
    const authored = editableRateInput(rate, defaultBasis);
    if (rate?.state === "known") {
        inputs[authored.basis] = authored;
        if (authored.basis === "rawWeight") {
            inputs.denormalizedWeight = Object.freeze({
                basis: "denormalizedWeight",
                value: decimalInputAt(
                    decimalInputParts(rate.value),
                    -DENORMALIZED_WEIGHT_DECIMAL_SHIFT,
                ),
            });
        } else if (authored.basis === "denormalizedWeight") {
            inputs.rawWeight = Object.freeze({
                basis: "rawWeight",
                value: decimalInputAt(
                    decimalInputParts(rate.value),
                    DENORMALIZED_WEIGHT_DECIMAL_SHIFT,
                ),
            });
        }
    }
    const basis = preferredBasis ?? authored.basis;
    return Object.freeze({
        basis,
        inputs: Object.freeze(inputs),
        value: inputs[basis].value,
    });
}
